import type { IFolder } from "@/types/folder";

import { FolderShareRole } from "@/constants/folders";
import { findFolderById } from "@/utils/findFolderById";

export function getFolderSharedRole(
  folder: IFolder,
  currentUserId?: number,
  folders: IFolder[] = []
): FolderShareRole | null {
  if (!currentUserId) {
    return null;
  }

  let current: IFolder | undefined = folder;
  const seen = new Set<number>();

  while (current) {
    if (seen.has(current.id)) {
      break;
    }
    seen.add(current.id);

    const role =
      current.sharedUsers?.find((user) => user.userId === currentUserId)
        ?.role ?? null;
    if (role) {
      return role;
    }

    if (!current.parentFolderId) {
      break;
    }

    current = findFolderById(folders, current.parentFolderId);
  }

  return null;
}

/**
 * Owner folders (`!isShared`) and shared EDIT can mutate structure:
 * rename, create subfolder, delete. Shared VIEW cannot.
 * Role may be inherited from an ancestor folder share.
 */
export function canEditFolder(
  folder: IFolder,
  currentUserId?: number,
  folders: IFolder[] = []
): boolean {
  if (currentUserId && folder.userOwnerId === currentUserId) {
    return true;
  }

  if (!folder.isShared) {
    return true;
  }

  return (
    getFolderSharedRole(folder, currentUserId, folders) ===
    FolderShareRole.RoleEdit
  );
}

/** Shared folder with effective VIEW role (including inherited from parent). */
export function isSharedViewOnly(
  folder: IFolder,
  currentUserId?: number,
  folders: IFolder[] = []
): boolean {
  if (!folder.isShared) {
    return false;
  }

  return (
    getFolderSharedRole(folder, currentUserId, folders) ===
    FolderShareRole.RoleView
  );
}

/** Owner-only actions: domain, change owner, etc. */
export function isFolderOwnerContext(folder: IFolder): boolean {
  return !folder.isShared;
}
